<!--
  - ===========================================================================
  - [StrongGate Limited] [http://www.stronggate.com]
  - Copyright (c) 2026. All rights Reserved.
  - Unauthorized access, use, dissemination, distribution, reproduction of this
  - file, or a portion of, in any form is prohibited and unlawful. If you receive
  - this file, and you are not an authorized company employee, or you believe you
  - have received this file in error, please notify the sender (or originator)
  - and promptly delete this file.
  -
  - Project:
  - File: nav-bar.vue
  - Created by: chan.ying
  - Create Date: 2026-04-27 15:50
  - Version Number: 1.0
  - Version Description:
  -
  - =============================================================================
  -->

<script>
import {SupportedLanguages, i18n, setCurrentLanguage} from "@/i18n";
import OnlineUserManager from "@/security/OnlineUserManager.ts";
import ProfileData from "@/utils/cookie/ProfileData";
import ZtnHeaderDropdown from "@/components/dropdown/ztn-header-dropdown.vue";
import Copy from "@/assets/icons/copy.vue";
import ZtnSearchBox from "@/components/input/ztn-search-box.vue";
import UserService from "@/services/UserService";
import {UserProfileInfo} from "@/mvc/models/team/UserInfoVO";
import {NotificationService} from "@/services/NotificationService";
import emitter from "@/utils/mitt/eventBus";
import {MessageType, ztnMessage} from "@/commons/utils/ZtnMessage";
import {DateUtil} from "@/commons/utils/DateUtil";
import ZtnRadio from "@/components/input/ztn-radio.vue";
import ZtnUserAvatar from "@/components/user/ztn-user-avatar.vue";
import {layoutMethods} from "@/state/helpers";

export default {
  components: {
    ZtnRadio,
    ZtnSearchBox,
    Copy,
    ZtnHeaderDropdown,
    ZtnUserAvatar
  },
  data() {
    return {
      languages: SupportedLanguages,
      fullscreen: true,
      msgCount: 0,
      inboxList: [],
    };
  },
  methods: {
    ...layoutMethods,
    closeAllDropdowns() {
      this.$refs.searchDropdown?.close()
      this.$refs.langDropdown?.close()
      this.$refs.msgDropdown?.close()
      this.$refs.profileDropdown?.close()
    },
    toggleMenu() {
      this.$parent.toggleMenu();
    },
    toggleRightSidebar() {
      this.$parent.toggleRightSidebar();
    },
    viewProfile() {
      this.$router.push("/profile");
    },
    viewMessage() {
      this.$router.push({name: 'notification-list'})
    },
    logout() {
      OnlineUserManager.Instance.logout();
    },
    initFullScreen() {
      this.fullscreen = !this.fullscreen;
      document.body.classList.toggle("fullscreen-enable");
      if (
          !document.fullscreenElement &&
          /* alternative standard method */ !document.mozFullScreenElement &&
          !document.webkitFullscreenElement
      ) {
        // current working methods
        if (document.documentElement.requestFullscreen) {
          document.documentElement.requestFullscreen();
        } else if (document.documentElement.mozRequestFullScreen) {
          document.documentElement.mozRequestFullScreen();
        } else if (document.documentElement.webkitRequestFullscreen) {
          document.documentElement.webkitRequestFullscreen(
              Element.ALLOW_KEYBOARD_INPUT
          );
        }
      } else {
        if (document.cancelFullScreen) {
          document.cancelFullScreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.webkitCancelFullScreen) {
          document.webkitCancelFullScreen();
        }
      }
    },
    changeAppearance(value) {
      UserService.Instance.updateUserProfile(
          this.currentUser.userInfo.userId,
          UserProfileInfo.setAppearance(value)
      ).then(response => {
        if (response.isHttpStatus2xx()) {
          ztnMessage({
            message: this.$t('data.profile.chg_theme_success'),
            type: MessageType.success
          });
          // ProfileData.setAppearance(this.theme);
        }
      });
    },
    changeLanguage(value) {
      UserService.Instance.updateUserProfile(
          this.currentUser.userInfo.userId,
          UserProfileInfo.setLanguage(value)
      ).then(response => {
        if (response.isHttpStatus2xx()) {
          ztnMessage({
            message: this.$t('data.profile.chg_lang_success'),
            type: MessageType.success
          });
          this.$store.dispatch('setLanguage', value);
          setCurrentLanguage(i18n, value)
        }
      });
    },
    showMesg() {
      NotificationService.Instance.list(null, false).then(res => {
        this.inboxList = res.data.data || []
      })
    },
    viewNotification(id) {
      this.$router.push({path: `/notification-detail/${id}`});
      this.closeAllDropdowns();
    }
  },
  computed: {
    DateUtil() {
      return DateUtil
    },
    currentUser() {
      return OnlineUserManager.Instance.get();
    },
    theme: {
      get() {
        return ProfileData.getAppearance();
      },
      set(value) {
        const themeStr = ProfileData.setAppearance(value);
        this.changeTheme({theme: themeStr});
      }
    },
    themeOptions() {
      return [
        {label: this.$t("data.profile.theme_light"), key: "light", value: ProfileData._ValueThemeLight},
        {label: this.$t("data.profile.theme_dark"), key: "dark", value: ProfileData._ValueThemeDark}
      ];
    },
  },
  created() {
    NotificationService.Instance.getUnreadCount().then(res => {
      this.msgCount = res.data
    })
    // keep a reference so beforeUnmount removes ONLY this handler —
    // emitter.off(type) without a handler would wipe every subscriber app-wide
    this.onUnreadCountUpdate = () => {
      NotificationService.Instance.getUnreadCount().then(res => {
        this.msgCount = res.data
      })
    }
    emitter.on('UPDATE_UNREAD_COUNT', this.onUnreadCountUpdate)
  },
  beforeUnmount() {
    emitter.off('UPDATE_UNREAD_COUNT', this.onUnreadCountUpdate)
  },
  watch: {
    '$route'() {
      this.$refs.searchDropdown?.close()
      this.$refs.langDropdown?.close()
      this.$refs.msgDropdown?.close()
      this.$refs.profileDropdown?.close()
    }
  }
};

</script>

<template>
  <header id="page-topbar" class="header">
    <div class="navbar-header">
      <div class="d-flex">
        <div class="navbar-brand-box">
          <router-link to="/" class="logo logo-dark" style="position: relative; left: -12px">
            <span class="logo-sm">
              <img src="@/assets/images/sg-logo-dark.png" alt height="40"/>
            </span>
            <span class="logo-lg">
              <img src="@/assets/images/sg-logo-dark.png" alt height="60"/>
              <strong class="strong-gate">STRONG-GATE</strong>
            </span>
          </router-link>
        </div>

        <button
            id="vertical-menu-btn"
            type="button"
            class="btn btn-sm px-3 font-size-16 header-item"
            @click="toggleMenu"
        >
          <i class="fa fa-fw fa-bars"></i>
        </button>

        <form class="app-search d-none d-lg-block">
          <ztn-search-box/>
        </form>
      </div>
      <div class="d-flex align-items-center gap-2">
        <ztn-header-dropdown class="d-inline-block d-lg-none" ref="searchDropdown" placement="bottom-center">
          <template #trigger>
            <div class="header-icon">
              <img src="@/assets/images/icon-png/search-icon.svg" width="24" height="24" alt=""/>
            </div>
          </template>
          <form class="p-3">
            <div class="form-group m-0">
              <div class="input-group">
                <input
                    type="text"
                    class="form-control"
                    :placeholder="$t('navbar.search.text')"
                    aria-label="Recipient's username"
                />
                <div class="input-group-append">
                  <button class="btn btn-primary" type="submit">
                    <i class="mdi mdi-magnify"></i>
                  </button>
                </div>
              </div>
            </div>
          </form>
        </ztn-header-dropdown>
        <ztn-header-dropdown width="160px" ref="langDropdown" placement="bottom-center">
          <template #trigger>
            <div class="current-language" style="position: relative; z-index: 2;">
              <img
                  :src="$store.getters.currentLanguageFlag"
                  :alt="$store.getters.currentLanguageTitle"
                  height="16"
              />
            </div>
          </template>
          <template #default>
            <div
                class="ztn-dropdown-item"
                v-for="(locale, i) in languages"
                :key="`locale-${i}`"
                @click="() => { changeLanguage(locale.value); closeAllDropdowns() }"
            >
              <img :src="locale.flag" :alt="locale.title" class="me-2" height="12"/>
              <span class="align-middle ztn-page-t400">{{ locale.title }}</span>
            </div>
          </template>
        </ztn-header-dropdown>
        <div class="dropdown">
          <button
              type="button"
              class="btn header-item noti-icon header-icon"
              @click="initFullScreen"
          >
            <i :class="['bx', fullscreen ? 'bx-fullscreen':'bx-exit-fullscreen']" id="fullscreen"></i>
          </button>
        </div>
        <ztn-header-dropdown width="380px" ref="msgDropdown" @open="showMesg">
          <template #trigger>
            <div class="mesg-icon-wrapper" style="position: relative; z-index: 2;">
              <img
                  :class="['mesg-img', { swinging: msgCount > 0 }]"
                  src="@/assets/images/icon-png/inform.svg"
                  alt=""
              />
              <span
                  v-if="msgCount > 0"
                  class="mesg-count badge bg-danger rounded-pill ztn-page-t200-condense"
              >
                {{ msgCount > 100 ? "99+" : msgCount }}
              </span>
            </div>
          </template>
          <template #body>
            <div class="notification-dropdown-header ztn-page-t300 d-flex justify-content-between align-items-center">
              <span>{{ $t("navbar.dropdown.notification.text") }}</span>
              <router-link :to="{ name: 'notification-list' }" @click="closeAllDropdowns">
                {{ $t("navbar.dropdown.notification.subtext") }}
              </router-link>
            </div>
            <div v-if="inboxList.length === 0" class="notification-empty ztn-page-t300">
              <i class="bx bx-bell-off"></i>
              <span>{{ $t("main.no_data") }}</span>
            </div>
            <div
                v-for="(item, index) in inboxList"
                :key="index"
                class="notification-item notification-item--unread"
                @click="viewNotification(item.userMessageId)"
            >
              <div class="notification-item__dot"></div>
              <div class="notification-item__content">
                <div class="notification-item__subject ztn-page-t300-strong">
                  {{ item.subject || item.brief }}
                </div>
                <!--                <div class="notification-item__brief ztn-page-t300" v-if="item.brief && item.brief !== 'test4'">-->
                <!--                  {{ item.brief }}-->
                <!--                </div>-->
                <div class="notification-item__time ztn-page-t200-condense">
                  {{ DateUtil.convertDatetime(item.modifiedAt) }}
                </div>
              </div>
            </div>
          </template>
        </ztn-header-dropdown>
        <ztn-header-dropdown width="240px" ref="profileDropdown">
          <template #trigger>
            <div class="d-flex align-items-center header-profile-trigger" style="position: relative; z-index: 2;">
              <ztn-user-avatar
                  class="me-2"
                  :src="currentUser.userInfo.avatar || ''"
                  :name="currentUser.userInfo.name || currentUser.userInfo.email"
                  :size="32"
              />
              <span class="ztn-page-t400 d-none d-xl-inline-block text-ellipsis">
                {{ currentUser.userInfo.email }}
              </span>
            </div>
          </template>
          <template #body>
            <div class="profile-dropdown-header mb-2">
              <ztn-user-avatar
                  class="profile-dropdown-header__avatar"
                  :src="currentUser.userInfo.avatar || ''"
                  :name="currentUser.userInfo.name || currentUser.userInfo.email"
                  :size="40"
              />
              <div class="profile-dropdown-header__info">
                <div class="profile-dropdown-header__email">
                  <span class="ztn-page-t300-strong text-ellipsis">{{ currentUser.userInfo.email }}</span>
                  <Copy :value="currentUser.userInfo.email" class="profile-dropdown-header__copy"/>
                </div>
                <div class="profile-dropdown-header__fqdn">
                  <span class="ztn-page-t200-condense text-ellipsis">{{
                      currentUser.teamInfo.teamFqdn
                    }}.ts.stronggate.com</span>
                  <Copy :value="`${currentUser.teamInfo.teamFqdn}.ts.stronggate.com`"
                        class="profile-dropdown-header__copy--small"/>
                </div>
              </div>
            </div>
            <div class="profile-dropdown-theme">
              <span class="ztn-page-t300">{{ $t('data.profile.theme') }}</span>
              <ztn-radio
                  v-model:value="theme"
                  :options="themeOptions"
                  @change="changeAppearance"
              />
            </div>
          </template>
          <template #default>
            <div class="profile-dropdown-actions">
              <div @click="() => { viewProfile(); closeAllDropdowns() }"
                   class="profile-dropdown-actions__item ztn-page-t300">
                <i class="bx bx-user font-size-16 me-1"></i>
                {{ $t("navbar.dropdown.henry.list.profile") }}
              </div>
              <div @click="() => { viewMessage(); closeAllDropdowns() }"
                   class="profile-dropdown-actions__item ztn-page-t300">
                <i class="bx bx-envelope font-size-16 me-1"></i>
                {{ $t("navbar.dropdown.henry.list.message") }}
              </div>
              <div class="profile-dropdown-divider"></div>
              <div @click="() => { logout(); closeAllDropdowns() }"
                   class="profile-dropdown-actions__item profile-dropdown-actions__item--danger ztn-page-t300">
                <i class="bx bx-log-in font-size-16 me-1"></i>
                {{ $t("navbar.dropdown.henry.list.logout") }}
              </div>
            </div>
          </template>
        </ztn-header-dropdown>
      </div>
    </div>
  </header>
</template>

<style scoped>
.header {
  background-color: var(--top-logo-bg) !important;
}

.strong-gate {
  color: var(--sidebar-primary);
  font-size: var(--fs-18);
}

.ztn-dropdown-default {
  cursor: auto;
}

.ztn-dropdown-item:hover {
  background: var(--body-aux-4);
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: var(--radius-sm);
  transition: background-color 0.2s;
}

.mesg-img {
  width: var(--icon-size-lg);
  height: var(--icon-size-lg);
}

@keyframes swing {
  0% {
    transform: rotate(0deg);
  }
  20% {
    transform: rotate(15deg);
  }
  40% {
    transform: rotate(-12deg);
  }
  60% {
    transform: rotate(8deg);
  }
  80% {
    transform: rotate(-5deg);
  }
  100% {
    transform: rotate(0deg);
  }
}

.mesg-img.swinging {
  transform-origin: top center;
  animation: swing 0.8s ease-in-out infinite;
}

.mesg-icon-wrapper {
  position: relative;
  padding: var(--space-1);
  display: inline-block;
}

.mesg-count {
  position: absolute;
  bottom: -4px;
  right: -8px;
  transform: translate(25%, 25%);
  font-size: var(--fs-11);
  min-width: 18px;
  height: 18px;
  line-height: 18px;
  padding: 0 var(--space-1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-profile-trigger {
  padding: var(--space-1);
  border-radius: var(--radius-sm);
  transition: background-color 0.2s;
}

.header-profile-user {
  width: 32px;
  height: var(--control-height-sm);
}

.navbar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 70px;
  padding: 0 var(--space-4);
}

.navbar-header > .d-flex:first-child {
  align-items: center;
  gap: var(--space-2);
}

.text-ellipsis {
  display: inline;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.header-profile-trigger .ztn-user-avatar {
  flex-shrink: 0;
}

#vertical-menu-btn {
  &::v-deep i {
    color: var(--body-color-1);
  }
}

/* Notification dropdown styles */
.notification-dropdown-header {
  padding: var(--space-3) var(--space-3) !important;
  border-bottom: 1px solid var(--body-aux-4);
  margin-bottom: 2px;
}

.notification-dropdown-header a {
  text-decoration: none;
  color: var(--base-primary-h);
  font-weight: 500;
  white-space: nowrap;
}

.notification-dropdown-header a:hover {
  text-decoration: underline;
}

.notification-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-5) var(--space-3) !important;
  color: var(--body-aux-2);
  gap: var(--space-2);
}

.notification-empty i {
  font-size: var(--fs-24);
  opacity: 0.5;
}

.notification-item {
  display: flex;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-3) !important;
  cursor: pointer;
  border-bottom: 1px solid var(--outline-variant);
  transition: background-color 0.15s ease;
}

.notification-item:last-child {
  border-bottom: none;
}

.notification-item:hover {
  background-color: var(--body-aux-2) !important;
}

.notification-item--unread .notification-item__subject {
  font-weight: 600;
}

.notification-item__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--base-primary-1);
  flex-shrink: 0;
  align-self: flex-start;
  margin-top: var(--space-2);
}

.notification-item__content {
  flex: 1;
  min-width: 0;
}

.notification-item__subject {
  margin-bottom: 2px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notification-item__brief {
  margin-bottom: var(--space-1);
  color: var(--body-aux-2);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}

.notification-item__time {
  font-size: var(--fs-11);
  white-space: nowrap;
}

/* Profile dropdown */
.profile-dropdown-header {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-4);
  border-bottom: 1px solid var(--body-aux-4);
}

.profile-dropdown-header__avatar {
  width: 40px;
  height: var(--control-height-lg);
  flex-shrink: 0;
}

.profile-dropdown-header__info {
  flex: 1;
  min-width: 0;
}

.profile-dropdown-header__email {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-bottom: var(--space-1);
}

.profile-dropdown-header__email span {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.profile-dropdown-header__fqdn {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  overflow: hidden;
}

.profile-dropdown-header__fqdn span {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.profile-dropdown-header__copy {
  flex-shrink: 0;
  cursor: pointer;
  opacity: 0.4;
  transition: opacity 0.15s;
}

.profile-dropdown-header__copy:hover {
  opacity: 1;
}

.profile-dropdown-header__copy--small {
  flex-shrink: 0;
  cursor: pointer;
  margin-left: var(--space-1);
  opacity: 0.4;
  transition: opacity 0.15s;
}

.profile-dropdown-header__copy--small:hover {
  opacity: 1;
}

/* Theme selector */
.profile-dropdown-theme {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--body-aux-4);
}

/* Action items */
.profile-dropdown-actions {
  padding: var(--space-1);
}

.profile-dropdown-actions__item {
  display: flex;
  align-items: center;
  padding: var(--space-2) var(--space-3);
  cursor: pointer;
  transition: background-color 0.15s;
  border-radius: var(--radius-sm);
  margin: 1px 0;
}

.profile-dropdown-actions__item:hover {
  background-color: var(--body-aux-4);
}

.profile-dropdown-actions__item--danger:hover {
  background-color: var(--base-bg-danger-8);
  color: var(--body-color-1);
}

.profile-dropdown-divider {
  height: 1px;
  margin: var(--space-1) var(--space-3);
  background: var(--body-aux-4);
}

/* Override parent dropdown global hover */
.profile-dropdown-actions :deep(.ztn-header-dropdown-menu > .profile-dropdown-actions > .profile-dropdown-actions__item) {
  background-color: transparent !important;
}

.profile-dropdown-actions :deep(.ztn-header-dropdown-menu > .profile-dropdown-actions > .profile-dropdown-actions__item:hover) {
  background-color: var(--body-aux-4) !important;
}

.profile-dropdown-actions :deep(.ztn-header-dropdown-menu > .profile-dropdown-actions > .profile-dropdown-actions__item--danger:hover) {
  background-color: var(--base-bg-danger-8) !important;
  color: var(--body-color-1) !important;
}

.text-ellipsis {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>